import { useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { useSeo } from "@/lib/useSeo";
import { PageHero } from "@/components/site/PageHero";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, ShieldCheck, Upload } from "lucide-react";

const HOTEL_OPTS = ["Standard", "Premium", "Luxury", "Mixed Category", "Need Recommendation"];
const TRANSPORT_OPTS = ["Sedan", "SUV", "Tempo Traveller", "Coach", "Need Recommendation"];
const RESPONSE_OPTS = ["Email", "Phone Call", "WhatsApp"];

function Field({ label, required, children, error, testid }) {
  return (
    <div className="space-y-1.5">
      <Label className="font-accent text-xs font-semibold text-charcoal">{label}{required && <span className="text-saffron"> *</span>}</Label>
      {children}
      {error && <p data-testid={testid ? `${testid}-error` : undefined} className="text-xs text-destructive">{error}</p>}
    </div>
  );
}

export default function SendQuery() {
  useSeo({
    title: "Send Your Query | Maharashtra B2B Travel Enquiry | Maha Sampada",
    description: "Submit your Maharashtra travel requirement. Get a customized itinerary and confidential B2B pricing from Maha Sampada's destination team.",
  });
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [errors, setErrors] = useState({});
  const [form, setForm] = useState({
    agency_name: "", agent_name: "", mobile: "", whatsapp: "", email: "",
    travel_dates: "", adults: "", children: "", children_ages: "", starting_city: "",
    destinations: params.get("ref") || "", nights: "", hotel_category: "", rooms: "",
    meal_preference: "", transportation: "", budget: "", travel_purpose: "",
    special_requirements: "", file_url: "", preferred_response: "",
    needs_design_help: false, consent: false,
  });

  const set = (k, v) => { setForm((f) => ({ ...f, [k]: v })); setErrors((e) => ({ ...e, [k]: undefined })); };

  const validate = () => {
    const e = {};
    if (!form.agency_name.trim()) e.agency_name = "Travel agency name is required";
    if (!form.agent_name.trim()) e.agent_name = "Agent name is required";
    if (!/^[0-9+\-\s]{7,15}$/.test(form.mobile.trim())) e.mobile = "Enter a valid mobile number";
    if (!/^\S+@\S+\.\S+$/.test(form.email.trim())) e.email = "Enter a valid email address";
    if (!form.destinations.trim()) e.destinations = "Please share preferred destinations";
    if (!form.consent) e.consent = "Please provide consent to proceed";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const onFile = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      set("file_url", data.file_url);
      toast.success("File uploaded");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Upload failed");
    } finally { setUploading(false); }
  };

  const submit = async (ev) => {
    ev.preventDefault();
    if (!validate()) {
      toast.error("Please fix the highlighted fields");
      setTimeout(() => document.querySelector('[data-testid$="-error"]')?.scrollIntoView({ behavior: "smooth", block: "center" }), 50);
      return;
    }
    setLoading(true);
    try {
      const payload = {
        ...form,
        adults: parseInt(form.adults) || 0,
        children: parseInt(form.children) || 0,
        nights: parseInt(form.nights) || 0,
        rooms: parseInt(form.rooms) || 0,
      };
      const { data } = await api.post("/enquiries", payload);
      toast.success("Enquiry submitted successfully");
      navigate(`/thank-you?type=query&ref=${encodeURIComponent(data.reference)}`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Submission failed. Please try again.");
    } finally { setLoading(false); }
  };

  return (
    <div>
      <PageHero overline="B2B Travel Enquiry" title="Send Your Query"
        subtitle="Share your client's requirement and our destination team will prepare a customized proposal with confidential B2B pricing."
        image="https://images.unsplash.com/photo-1695453461885-bcecb1c64320?crop=entropy&cs=srgb&fm=jpg&q=85&w=2000"
        crumbs={[{ label: "Send Your Query" }]} height="h-[46vh]" />

      <section className="mx-auto max-w-4xl px-4 py-16 sm:px-6">
        <form onSubmit={submit} data-testid="enquiry-form" className="rounded-3xl border border-gold/25 bg-white p-6 sm:p-10">
          <h2 className="font-display text-2xl font-semibold text-charcoal">Travel Partner Details</h2>
          <div className="mt-6 grid gap-5 sm:grid-cols-2">
            <Field label="Travel Agency Name" required error={errors.agency_name} testid="agency_name"><Input data-testid="input-agency-name" value={form.agency_name} onChange={(e) => set("agency_name", e.target.value)} /></Field>
            <Field label="Agent Name" required error={errors.agent_name} testid="agent_name"><Input data-testid="input-agent-name" value={form.agent_name} onChange={(e) => set("agent_name", e.target.value)} /></Field>
            <Field label="Mobile Number" required error={errors.mobile} testid="mobile"><Input data-testid="input-mobile" value={form.mobile} onChange={(e) => set("mobile", e.target.value)} placeholder="+91 …" /></Field>
            <Field label="WhatsApp Number" error={errors.whatsapp}><Input data-testid="input-whatsapp" value={form.whatsapp} onChange={(e) => set("whatsapp", e.target.value)} /></Field>
            <Field label="Email Address" required error={errors.email} testid="email"><Input data-testid="input-email" type="email" value={form.email} onChange={(e) => set("email", e.target.value)} /></Field>
            <Field label="Starting City"><Input data-testid="input-starting-city" value={form.starting_city} onChange={(e) => set("starting_city", e.target.value)} /></Field>
          </div>

          <h2 className="mt-10 font-display text-2xl font-semibold text-charcoal">Travel Requirement</h2>
          <div className="mt-6 grid gap-5 sm:grid-cols-2">
            <Field label="Guest Travel Dates"><Input data-testid="input-travel-dates" value={form.travel_dates} onChange={(e) => set("travel_dates", e.target.value)} placeholder="e.g. 12–18 Nov 2026" /></Field>
            <Field label="Destinations Preferred" required error={errors.destinations} testid="destinations"><Input data-testid="input-destinations" value={form.destinations} onChange={(e) => set("destinations", e.target.value)} placeholder="e.g. Konkan, Mahabaleshwar" /></Field>
            <Field label="Number of Adults"><Input data-testid="input-adults" type="number" min="0" value={form.adults} onChange={(e) => set("adults", e.target.value)} /></Field>
            <Field label="Number of Children"><Input data-testid="input-children" type="number" min="0" value={form.children} onChange={(e) => set("children", e.target.value)} /></Field>
            <Field label="Children's Ages"><Input data-testid="input-children-ages" value={form.children_ages} onChange={(e) => set("children_ages", e.target.value)} placeholder="e.g. 6, 9" /></Field>
            <Field label="Number of Nights"><Input data-testid="input-nights" type="number" min="0" value={form.nights} onChange={(e) => set("nights", e.target.value)} /></Field>
            <Field label="Number of Rooms"><Input data-testid="input-rooms" type="number" min="0" value={form.rooms} onChange={(e) => set("rooms", e.target.value)} /></Field>
            <Field label="Hotel Category">
              <Select value={form.hotel_category} onValueChange={(v) => set("hotel_category", v)}>
                <SelectTrigger data-testid="select-hotel-category"><SelectValue placeholder="Select category" /></SelectTrigger>
                <SelectContent>{HOTEL_OPTS.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            <Field label="Transportation Requirement">
              <Select value={form.transportation} onValueChange={(v) => set("transportation", v)}>
                <SelectTrigger data-testid="select-transportation"><SelectValue placeholder="Select transport" /></SelectTrigger>
                <SelectContent>{TRANSPORT_OPTS.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            <Field label="Meal Preference"><Input data-testid="input-meal" value={form.meal_preference} onChange={(e) => set("meal_preference", e.target.value)} placeholder="e.g. Breakfast only / Veg" /></Field>
            <Field label="Approximate Budget"><Input data-testid="input-budget" value={form.budget} onChange={(e) => set("budget", e.target.value)} placeholder="Per person or total" /></Field>
            <Field label="Travel Purpose"><Input data-testid="input-purpose" value={form.travel_purpose} onChange={(e) => set("travel_purpose", e.target.value)} placeholder="Family / Honeymoon / Group…" /></Field>
            <Field label="Preferred Response Method">
              <Select value={form.preferred_response} onValueChange={(v) => set("preferred_response", v)}>
                <SelectTrigger data-testid="select-response"><SelectValue placeholder="How should we reach you?" /></SelectTrigger>
                <SelectContent>{RESPONSE_OPTS.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
          </div>

          <div className="mt-5 grid gap-5">
            <Field label="Special Requirements"><Textarea data-testid="input-special" rows={3} value={form.special_requirements} onChange={(e) => set("special_requirements", e.target.value)} placeholder="Accessibility, celebrations, dietary needs, etc." /></Field>
            <Field label="Attach a File (optional)">
              <label className="flex cursor-pointer items-center gap-3 rounded-lg border border-dashed border-gold/50 bg-cream px-4 py-3 text-sm text-muted-foreground hover:border-saffron">
                {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4 text-saffron" />}
                <span data-testid="file-status">{form.file_url ? "File attached ✓" : uploading ? "Uploading…" : "Upload requirement sheet, passenger list, etc. (max 10MB)"}</span>
                <input data-testid="input-file" type="file" className="hidden" onChange={(e) => onFile(e.target.files?.[0])} />
              </label>
            </Field>
          </div>

          <div className="mt-6 space-y-4 rounded-2xl bg-cream p-5">
            <label className="flex items-start gap-3">
              <Checkbox data-testid="checkbox-design-help" checked={form.needs_design_help} onCheckedChange={(v) => set("needs_design_help", !!v)} className="mt-0.5" />
              <span className="text-sm text-charcoal">I need assistance designing the itinerary.</span>
            </label>
            <label className="flex items-start gap-3">
              <Checkbox data-testid="checkbox-consent" checked={form.consent} onCheckedChange={(v) => set("consent", !!v)} className="mt-0.5" />
              <span className="text-sm text-charcoal">I consent to Maha Sampada contacting me about this enquiry and storing these details.<span className="text-saffron"> *</span></span>
            </label>
            {errors.consent && <p data-testid="consent-error" className="text-xs text-destructive">{errors.consent}</p>}
          </div>

          <div className="mt-8 flex items-center justify-between gap-4">
            <p className="flex items-center gap-1.5 text-xs text-muted-foreground"><ShieldCheck className="h-4 w-4 text-forest" />Confidential B2B enquiry. No prices are published.</p>
            <Button type="submit" data-testid="submit-enquiry" disabled={loading || uploading} className="rounded-full bg-saffron px-8 font-accent font-semibold text-white hover:bg-saffron-dark">
              {loading ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Submitting…</> : "Submit Query"}
            </Button>
          </div>
        </form>
        <p className="mt-4 text-center text-xs text-muted-foreground">Prefer to register as a partner? <Link to="/partner" className="text-saffron hover:underline">Become a Travel Partner</Link></p>
      </section>
    </div>
  );
}
